import { useCallback } from 'react';
import type { ToastProps } from '~/components/granular/toast/toast.types';
import { useToast } from '~/global-state/toast';
import { type FormatMessage, useFormatMessage } from '~/intl';
import type { HttpErrorDetail } from '~/schemas/errors';
import { httpErrorDetailsSchema } from '~/schemas/errors';
import { ApiError } from './api-error';
import { InvalidRequestSchemaError } from './invalid-request-schema';
import { InvalidResponseSchemaError } from './invalid-response-schema';

export interface ApiErrorInformation {
  errorTitle: string;
  errorDescription?: string[];
  errorRequestId?: string;
}

interface HandleApiErrorOptions {
  error: unknown;
  fallbackMessage?: string;
  showToast?: boolean;
}

export const handleApiError = (
  options: HandleApiErrorOptions,
  { addToast, formatMessage }: { addToast: (props: ToastProps) => void; formatMessage: FormatMessage },
): ApiErrorInformation => {
  const { error, fallbackMessage = formatMessage({ id: 'error.generic' }), showToast } = options;

  let errorTitle = fallbackMessage;
  let errorDescription: string[] | undefined;
  let errorRequestId: string | undefined;

  if (error instanceof ApiError) {
    const { message, detail, requestId } = error;
    errorTitle = withHttpStatus(error, message || fallbackMessage);
    errorRequestId = requestId;
    errorDescription = getErrorDetail(detail);
  } else if (error instanceof InvalidRequestSchemaError) {
    errorTitle = 'Invalid request schema';
  } else if (error instanceof InvalidResponseSchemaError) {
    errorTitle = 'Invalid response schema';
  }

  if (showToast) {
    addToast({
      appearance: 'error',
      copyText: errorRequestId,
      description: errorDescription?.join(', '),
      id: `api-error-${crypto.randomUUID()}`,
      title: errorTitle,
    });
  }

  if (import.meta.env.DEV) {
    console.error('API Error:', {
      error,
      errorDescription,
      errorRequestId,
      errorTitle,
    });
  }

  return { errorDescription, errorRequestId, errorTitle };
};

export function withHttpStatus(error: unknown, title: string) {
  return error instanceof ApiError ? `[${error.status}] - ${title}` : title;
}

export function useHandleApiError() {
  const { addToast } = useToast();
  const formatMessage = useFormatMessage();

  return useCallback(
    (options: HandleApiErrorOptions): ApiErrorInformation => handleApiError(options, { addToast, formatMessage }),
    [addToast, formatMessage],
  );
}

export function detailToString(detail: HttpErrorDetail) {
  return detail.loc?.length ? `${detail.msg} ${detail.loc[detail.loc.length - 1]}` : detail.msg;
}

function getErrorDetail(detail: unknown, schema = httpErrorDetailsSchema) {
  const errorDetail = schema.safeParse(detail);

  if (errorDetail.success) {
    return errorDetail.data.map(detailToString);
  }
}
